<script setup lang="ts">
import { onMounted, ref } from 'vue';
import ArrowIcon from '@assets/icons/arrow.svg';
import type { EmblaCarouselType } from 'embla-carousel';
import emblaCarouselVue from 'embla-carousel-vue';

const props = withDefaults(defineProps<{
    paginationClass: string
}>(), {
    paginationClass: 'flex'
})

const [emblaRef, emblaApi] = emblaCarouselVue({
    slidesToScroll: 1,
    breakpoints: {
        '(min-width: 768px)': { slidesToScroll: 2 },
    },
});

const canScrollNext = ref(true);
const canScrollPrev = ref(true);
const selectedIndex = ref(0);
const scrollSnaps = ref<number[]>([]);

const onSelect = (api: EmblaCarouselType) => {
    canScrollNext.value = api.canScrollNext();
    canScrollPrev.value = api.canScrollPrev();
    selectedIndex.value = api.selectedScrollSnap();
    scrollSnaps.value = api.scrollSnapList();
};

const scrollNext = () => {
    emblaApi.value?.scrollNext();
};

const scrollPrev = () => {
    emblaApi.value?.scrollPrev();
};

onMounted(() => {
    if (!emblaApi.value) return;

    emblaApi.value.on('init', onSelect);
    emblaApi.value.on('reInit', onSelect);
    emblaApi.value.on('select', onSelect);
});
</script>

<template>
    <div class="relative">
        <div
            class="overflow-hidden"
            ref="emblaRef"
        >
            <div class="flex gap-y-20">
                <slot />
            </div>
        </div>

        <div
            :class="paginationClass"
            class="text-primary items-center justify-center gap-10 mt-4 md:mt-8"
        >
            <button
                class="active:text-foreground focus-visible:border-ring flex size-16 sm:size-24 cursor-pointer items-center justify-center rounded-full border-2 border-transparent hover:border-current focus:outline-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent"
                @click="scrollPrev"
                aria-label="Vorherige Seite"
                :disabled="!canScrollPrev"
            >
                <ArrowIcon class="-rotate-90" />
            </button>

            <ul class="hidden gap-x-2 sm:flex md:gap-x-5">
                <li
                    v-for="(_, index) in scrollSnaps"
                    :key="index"
                >
                    <button
                        @click="emblaApi?.scrollTo(index)"
                        class="focus-visible:ring-ring size-5 rounded-full border-2 focus:outline-0 focus-visible:ring-2 focus-visible:ring-offset-2"
                        :class="{
                            'border-primary bg-primary':
                                selectedIndex === index,
                            'border-primary bg-transparent':
                                selectedIndex !== index,
                        }"
                    >
                        <span class="sr-only">Seite {{ index }}</span>
                    </button>
                </li>
            </ul>

            <button
                class="active:text-foreground focus-visible:border-ring flex size-16 sm:size-24 cursor-pointer items-center justify-center rounded-full border-2 border-transparent hover:border-current focus:outline-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent"
                @click="scrollNext"
                aria-label="Nächste Seite"
                :disabled="!canScrollNext"
            >
                <ArrowIcon class="rotate-90" />
            </button>
        </div>
    </div>
</template>
